import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { getRequestFormPublic, submitRequestForm } from '../services/firestore';
import { FiSend, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const PublicFormPage = () => {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});

  useEffect(() => {
    if (!formId) return;
    getRequestFormPublic(formId).then(f => {
      setForm(f);
      if (f) {
        const initial = {};
        (f.fields || []).forEach(field => { initial[field.id] = ''; });
        setValues(initial);
      }
      setLoading(false);
    }).catch(() => { setError('Error al cargar formulario'); setLoading(false); });
  }, [formId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    for (const field of (form.fields || [])) {
      if (field.required && !values[field.id]?.trim()) {
        setError(`El campo "${field.label}" es obligatorio`);
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitRequestForm(formId, values);
      setSubmitted(true);
    } catch {
      setError('Error al enviar solicitud. Intenta de nuevo.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle size={40} className="text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-700">Formulario no encontrado</h2>
          <p className="text-sm text-gray-500 mt-1">Este formulario no existe o ha sido desactivado.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiCheckCircle size={48} className="text-green-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-700">Solicitud enviada</h2>
          <p className="text-sm text-gray-500 mt-1">Tu solicitud ha sido recibida correctamente.</p>
          <button
            onClick={() => { setSubmitted(false); setValues({}); const initial = {}; (form.fields || []).forEach(f => { initial[f.id] = ''; }); setValues(initial); }}
            className="mt-4 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Enviar otra solicitud
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-5">
            <h1 className="text-xl font-bold text-white">{form.name}</h1>
            {form.description && <p className="text-blue-100 text-sm mt-1">{form.description}</p>}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Submitter info */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tu nombre</label>
              <input
                type="text"
                value={values._submitterName || ''}
                onChange={(e) => setValues(v => ({ ...v, _submitterName: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="Nombre (opcional)"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tu email</label>
              <input
                type="email"
                value={values._submitterEmail || ''}
                onChange={(e) => setValues(v => ({ ...v, _submitterEmail: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="Email (opcional)"
              />
            </div>

            <hr className="border-gray-100" />

            {/* Dynamic fields */}
            {(form.fields || []).map(field => (
              <div key={field.id}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={values[field.id] || ''}
                    onChange={(e) => setValues(v => ({ ...v, [field.id]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-y"
                    rows={3}
                    placeholder={field.placeholder || ''}
                  />
                ) : field.type === 'select' ? (
                  <select
                    value={values[field.id] || ''}
                    onChange={(e) => setValues(v => ({ ...v, [field.id]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  >
                    <option value="">Seleccionar...</option>
                    {(field.options || []).map(opt => (
                      <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                    ))}
                  </select>
                ) : field.type === 'date' ? (
                  <input
                    type="date"
                    value={values[field.id] || ''}
                    onChange={(e) => setValues(v => ({ ...v, [field.id]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  />
                ) : (
                  <input
                    type="text"
                    value={values[field.id] || ''}
                    onChange={(e) => setValues(v => ({ ...v, [field.id]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    placeholder={field.placeholder || ''}
                  />
                )}
              </div>
            ))}

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
                <FiAlertCircle size={14} /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FiSend size={14} />
              {submitting ? 'Enviando...' : 'Enviar solicitud'}
            </button>
          </form>
        </div>
        <p className="text-center text-[10px] text-gray-400 mt-4">Formulario creado con Gestor de Tareas</p>
      </div>
    </div>
  );
};

export default PublicFormPage;
